import { useMemo } from 'react'
import { useLingui } from '@lingui/react/macro'

import type { GuildIconKeys } from '@newComponents/GuildIcon/GuildIcon'

export type DocPage = {
  path: string
  label: string
}

export type DocSectionConfig = {
  path: string
  label: string
  icon: GuildIconKeys
  description: string
  pages: readonly DocPage[]
}

export const useDocSections = (): readonly DocSectionConfig[] => {
  const { t } = useLingui()

  return useMemo(
    () =>
      [
        {
          path: 'getting-started',
          label: t`Getting Started`,
          icon: 'star',
          description: t`Learn the basics of using Guild.`,
          pages: [
            { path: 'overview', label: t`Overview` },
            { path: 'command-menu', label: t`Command Palette` },
            { path: 'search', label: t`Search` },
            { path: 'navigation', label: t`Navigation` },
          ],
        },
        {
          path: 'guilds',
          label: t`Guilds`,
          icon: 'people',
          description: t`Create and manage your community.`,
          pages: [
            { path: 'overview', label: t`Overview` },
            { path: 'creation', label: t`Creating a Guild` },
            { path: 'profile', label: t`Guild Profile` },
            { path: 'settings', label: t`Guild Settings` },
            { path: 'members', label: t`Managing Members` },
            { path: 'forms', label: t`Membership Forms` },
            { path: 'monetization', label: t`Monetization` },
            { path: 'messaging', label: t`Messaging` },
            { path: 'embeds', label: t`Embeds` },
            { path: 'bluesky', label: t`Bluesky` },
            { path: 'google-calendar', label: t`Google Calendar` },
            { path: 'meetup', label: t`Meetup Event Import` },
            { path: 'calendar-feeds', label: t`Calendar Feeds` },
          ],
        },
        {
          path: 'events',
          label: t`Events`,
          icon: 'calendar',
          description: t`Host gatherings with ticketing, RSVPs, waitlists, and attendee messaging.`,
          pages: [
            { path: 'overview', label: t`Overview` },
            { path: 'creation', label: t`Creating an Event` },
            { path: 'page', label: t`Event Page` },
            { path: 'settings', label: t`Event Settings` },
            { path: 'multi-host', label: t`Multi-Host Events` },
            { path: 'rsvps', label: t`RSVPs & Attendees` },
            { path: 'attendee-forms', label: t`Attendee Forms` },
            { path: 'visibility', label: t`Visibility` },
            { path: 'ticketing-overview', label: t`Ticketing Overview` },
            { path: 'stripe', label: t`Setting Up Stripe` },
            { path: 'tiers', label: t`Ticketing Tiers` },
            { path: 'discounts', label: t`Discount Codes` },
            { path: 'orders', label: t`Orders & Refunds` },
            { path: 'purchase-flow', label: t`Attendee Purchase Flow` },
            { path: 'messages', label: t`Event Messages` },
            { path: 'embeds', label: t`Embeds` },
          ],
        },
        {
          path: 'presentations',
          label: t`Presentations`,
          icon: 'projection-screen',
          description: t`Share talks, slides, and recordings.`,
          pages: [
            { path: 'overview', label: t`Overview` },
            { path: 'creation', label: t`Creating a Presentation` },
            { path: 'claiming', label: t`Claiming a Presentation` },
            { path: 'page', label: t`Presentation Page` },
            { path: 'embeds', label: t`Embeds` },
          ],
        },
        {
          path: 'account',
          label: t`Your Account`,
          icon: 'person',
          description: t`Manage your profile, settings, and security.`,
          pages: [
            { path: 'overview', label: t`Overview` },
            { path: 'sign-in', label: t`Sign Up & Sign In` },
            { path: 'profile', label: t`Your Profile` },
            { path: 'joining', label: t`Joining a Guild` },
            { path: 'notifications', label: t`Notification Settings` },
            { path: 'security', label: t`Authentication & Security` },
            { path: 'calendar', label: t`Calendar Sync` },
            { path: 'embeds', label: t`Embeds` },
          ],
        },
        {
          path: 'developers',
          label: t`Developers`,
          icon: 'link',
          description: t`Build integrations with Guild's OAuth 2.0 API.`,
          pages: [{ path: 'oauth', label: t`OAuth` }],
        },
      ] as const,
    [t]
  )
}
